import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaSearch,
  FaSlidersH,
  FaTimes,
  FaUserCheck,
  FaUserFriends,
  FaUserTimes,
} from "react-icons/fa";
import {
  fetchContactsPaginated,
  fetchRemainingQuota,
  updateRoundRobin,
  uploadContactsCsv,
} from "../../api/contacts.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { parseCsv } from "../../utils/parseCsv";
import WhatsAppConnectPrompt from "./WhatsAppConnectPrompt";
import StatusCapsule from "../StatusCapsule";

const SAMPLE_CONTACTS_CSV = `name,mobile,tags
John Doe,9876543210,VIP
Jane Smith,9123456789,Lead`;

const IMPORT_FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "mobile_number_id", label: "Mobile Number", required: true },
  { key: "tags", label: "Tags", required: false },
];

const guessHeader = (headers, fieldKey) => {
  const match = (pattern) =>
    headers.find((header) => pattern.test(header.trim())) || "";
  if (fieldKey === "name") return match(/^(name|username|contact.?name)$/i);
  if (fieldKey === "mobile_number_id") {
    return match(/^(mobile|waid|phone|mobile.?number|phone.?number)$/i);
  }
  if (fieldKey === "tags") return match(/^tags?$/i);
  return "";
};

const buildInitialMappings = (headers) =>
  IMPORT_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field.key]: guessHeader(headers, field.key),
    }),
    { name: "", mobile_number_id: "", tags: "" },
  );

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const formatMobile = (waid) => {
  if (waid == null || waid === "") return "";
  const s = String(waid);
  return s.startsWith("+") ? s : `+${s}`;
};

const formatLastActive = (value) => {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const ms = num < 1e12 ? num * 1000 : num;
  try {
    return new Date(ms).toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatFirstSeen = (createdAt) => {
  if (!createdAt) return "";
  try {
    return new Date(createdAt).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
};

const mapCustomer = (row) => ({
  id: row._id,
  name: row.name || "—",
  mobile: formatMobile(row.waid),
  status: row.status || row.waConversation || "—",
  intervenedBy: row.IntervenedByName || "",
  lastActive: formatLastActive(row.lastActive),
  tags: Array.isArray(row.tags)
    ? row.tags.map((t) => t.name || t).filter(Boolean).join(", ")
    : "",
  source: row.source || "",
  firstSeen: formatFirstSeen(row.createdAt),
});

const Contacts = () => {
  const {
    projectId,
    phoneNoId,
    wabaId,
    phoneReady,
    projectLoading,
    needsWhatsAppSetup,
  } = useProjectPhone();

  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [optedIn, setOptedIn] = useState(0);
  const [optedOut, setOptedOut] = useState(0);
  const [remainingQuota, setRemainingQuota] = useState(null);
  const [roundRobin, setRoundRobin] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rrUpdating, setRrUpdating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importName, setImportName] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  const [importMappings, setImportMappings] = useState({
    name: "",
    mobile_number_id: "",
    tags: "",
  });
  const [countryCode, setCountryCode] = useState("91");
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchContacts = useCallback(async () => {
    if (projectLoading) return;
    if (!phoneNoId) {
      setContacts([]);
      setTotal(0);
      setOptedIn(0);
      setOptedOut(0);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchContactsPaginated(phoneNoId, {
        page,
        limit: perPage,
        search: debouncedQuery,
      });
      const list = res?.customers || res?.data?.customers || [];
      setContacts(Array.isArray(list) ? list.map(mapCustomer) : []);
      setTotal(res?.total ?? res?.data?.total ?? 0);
      setOptedIn(res?.totaloptedIn ?? res?.data?.totaloptedIn ?? 0);
      setOptedOut(res?.totaloptedOut ?? res?.data?.totaloptedOut ?? 0);
      if (typeof res?.isRoundRobinAssignment === "boolean") {
        setRoundRobin(res.isRoundRobinAssignment);
      }
    } catch (err) {
      setContacts([]);
      setError(err.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [phoneNoId, projectLoading, page, perPage, debouncedQuery]);

  const fetchQuota = useCallback(async () => {
    if (projectLoading || !phoneNoId) return;
    try {
      const res = await fetchRemainingQuota(phoneNoId);
      if (res?.success) {
        setRemainingQuota(res.remainingQuota ?? null);
      } else if (res?.message) {
        setError(res.message);
      }
    } catch (err) {
      setRemainingQuota(null);
      setError(err.message || "Failed to load remaining quota");
    }
  }, [phoneNoId, projectLoading]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const allChecked =
    contacts.length > 0 && selected.size === contacts.length;

  const toggleAll = () => {
    setSelected((prev) => {
      if (contacts.length === 0) return new Set();
      if (prev.size === contacts.length) return new Set();
      return new Set(contacts.map((c) => c.id));
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRoundRobin = async () => {
    if (!projectId || rrUpdating) return;
    const next = !roundRobin;
    setRrUpdating(true);
    try {
      await updateRoundRobin(projectId, next);
      setRoundRobin(next);
    } catch (err) {
      setError(err.message || "Failed to update round robin assignment");
    } finally {
      setRrUpdating(false);
    }
  };

  const quotaLabel = useMemo(() => {
    if (remainingQuota == null) return "—";
    return String(remainingQuota);
  }, [remainingQuota]);

  const resetImportForm = () => {
    setImportName("");
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRowCount(0);
    setImportMappings({ name: "", mobile_number_id: "", tags: "" });
    setCountryCode("91");
    setImportError("");
    setImportSuccess("");
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const openImportModal = () => {
    resetImportForm();
    setImportOpen(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setImportOpen(false);
    resetImportForm();
  };

  const onCsvSelected = (file) => {
    if (!file) return;
    setImportError("");
    setImportSuccess("");
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = String(event.target?.result || "");
      const { headers, rows } = parseCsv(text);
      setCsvHeaders(headers);
      setCsvRowCount(rows.length);
      setImportMappings(buildInitialMappings(headers));
    };
    reader.readAsText(file);
  };

  const onMappingChange = (fieldKey, value) => {
    setImportMappings((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const downloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_CONTACTS_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "sample_contacts.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const onImportSubmit = async (event) => {
    event.preventDefault();
    if (!csvFile) {
      setImportError("Please select a CSV file.");
      return;
    }
    if (!importMappings.name || !importMappings.mobile_number_id) {
      setImportError("Please map Name and Mobile Number columns.");
      return;
    }
    if (!phoneNoId || !projectId) {
      setImportError("Project details are missing. Reload the page and try again.");
      return;
    }

    setImporting(true);
    setImportError("");
    setImportSuccess("");

    try {
      const res = await uploadContactsCsv({
        csvFile,
        mappings: importMappings,
        phoneNoId,
        wabaId,
        countryCode: countryCode.trim(),
        projectId,
      });
      if (res?.success) {
        setImportSuccess(res.message || "Contacts imported successfully.");
        await fetchContacts();
        setTimeout(() => {
          setImportOpen(false);
          resetImportForm();
        }, 1200);
      } else {
        setImportError(res?.message || "Failed to import contacts.");
      }
    } catch (err) {
      setImportError(err.message || "An error occurred while uploading the CSV.");
    } finally {
      setImporting(false);
    }
  };

  if (projectLoading) {
    return (
      <div className="contacts-page">
        <p className="contacts-empty">Loading project…</p>
      </div>
    );
  }

  if (needsWhatsAppSetup) {
    return (
      <div className="contacts-page">
        <WhatsAppConnectPrompt
          projectId={projectId}
          title="Connect WhatsApp to manage contacts"
          description="Contacts are loaded from your linked WhatsApp Business number. Connect your number in project settings to import and manage leads."
        />
      </div>
    );
  }

  return (
    <div className="contacts-page">
      <div className="contacts-topstrip">
        <div className="contacts-title">
          <FaUserFriends />
          <div>
            <h1>Contacts</h1>
            <p>
              Turn conversations into opportunities by capturing and managing
              WhatsApp leads effortlessly.
            </p>
          </div>
        </div>

        <div className="contacts-strip-controls">
          <div className="contacts-rr">
            <span>Round Robin Assignment</span>
            <button
              type="button"
              className={`contacts-toggle ${roundRobin ? "on" : ""}`}
              onClick={toggleRoundRobin}
              disabled={rrUpdating}
              aria-pressed={roundRobin}
            >
              <span className="contacts-toggle-knob" />
            </button>
          </div>

          <div className="contacts-strip-metrics">
            <div className="contacts-metric">
              <span>Quality Rating</span>
              <span className="contacts-pill contacts-pill-green">GREEN</span>
            </div>
            <div className="contacts-metric">
              <span>Template Messaging Tier</span>
              <span className="contacts-pill contacts-pill-dark">
                TIER_100K
              </span>
            </div>
            <div className="contacts-metric">
              <span>Remaining Quota</span>
              <span className="contacts-pill contacts-pill-dark">
                {quotaLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="contacts-summary">
        <div className="contacts-summary-card contacts-summary-total">
          <div className="contacts-summary-icon">
            <FaUserFriends />
          </div>
          <div className="contacts-summary-copy">
            <span>Total Contacts</span>
            <strong>{total}</strong>
          </div>
        </div>
        <div className="contacts-summary-card contacts-summary-in">
          <div className="contacts-summary-icon">
            <FaUserCheck />
          </div>
          <div className="contacts-summary-copy">
            <span>Opted-In Contacts</span>
            <strong>{optedIn}</strong>
          </div>
        </div>
        <div className="contacts-summary-card contacts-summary-out">
          <div className="contacts-summary-icon">
            <FaUserTimes />
          </div>
          <div className="contacts-summary-copy">
            <span>Opted-Out Contacts</span>
            <strong>{optedOut}</strong>
          </div>
        </div>
        <button
          type="button"
          className="contacts-import-btn"
          onClick={openImportModal}
        >
          Add or Import (Contacts)
        </button>
      </div>

      <div className="contacts-toolbar">
        <div className="contacts-search">
          <FaSearch className="contacts-search-icon" />
          <input
            type="search"
            placeholder="Search by name or phone number"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="contacts-filter" aria-label="Filter">
            <FaSlidersH />
          </button>
        </div>

        <div className="contacts-actions">
          <button type="button" className="contacts-action" disabled>
            Export Contact
          </button>
          <button type="button" className="contacts-action" disabled>
            Archived
          </button>
          <button type="button" className="contacts-action" disabled>
            Broadcast
          </button>
          <button type="button" className="contacts-action" disabled>
            Edit
          </button>
        </div>
      </div>

      {error && <p className="biz-panel-error contacts-error">{error}</p>}

      <div className="contacts-table-wrap">
        <table className="contacts-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>Name</th>
              <th>Mobile Number</th>
              <th>Status</th>
              <th>Intervened By</th>
              <th>LastActive</th>
              <th>Tags</th>
              <th>Source</th>
              <th>First</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="contacts-empty">
                  Loading contacts…
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan={9} className="contacts-empty">
                  No contacts available.
                </td>
              </tr>
            ) : (
              contacts.map((c) => (
                <tr key={c.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      aria-label={`Select ${c.name}`}
                    />
                  </td>
                  <td>{c.name}</td>
                  <td>{c.mobile}</td>
                  <td className="contacts-status">
                    <StatusCapsule value={c.status} />
                  </td>
                  <td>{c.intervenedBy || ""}</td>
                  <td>{c.lastActive}</td>
                  <td>{c.tags}</td>
                  <td>{c.source}</td>
                  <td>{c.firstSeen}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <div
          className="biz-modal-backdrop contacts-import-backdrop"
          onClick={closeImportModal}
          role="presentation"
        >
          <div
            className="biz-modal-card contacts-import-modal"
            onClick={(ev) => ev.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="contacts-import-title"
          >
            <button
              type="button"
              className="contacts-import-close"
              onClick={closeImportModal}
              disabled={importing}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h2 id="contacts-import-title" className="contacts-import-title">
              Import Contacts
            </h2>
            <p className="contacts-import-subtitle">
              Upload a CSV file with contact name, mobile number, and optional tags.
            </p>

            <form className="contacts-import-form" onSubmit={onImportSubmit}>
              {importError && <p className="biz-form-error">{importError}</p>}
              {importSuccess && (
                <p className="biz-form-success">{importSuccess}</p>
              )}

              <label className="contacts-import-field">
                Name
                <input
                  type="text"
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                  placeholder="Enter name"
                  disabled={importing}
                />
              </label>

              <div className="contacts-import-file-row">
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="contacts-import-file-input"
                  onChange={(e) => onCsvSelected(e.target.files?.[0])}
                  disabled={importing}
                />
                <button
                  type="button"
                  className="contacts-import-browse-btn"
                  onClick={() => csvInputRef.current?.click()}
                  disabled={importing}
                >
                  Browse CSV
                </button>
                {csvFile && (
                  <span className="contacts-import-file-name">
                    {csvFile.name}
                    {csvRowCount > 0 ? ` · ${csvRowCount} rows` : ""}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="contacts-import-sample-link"
                onClick={downloadSampleCsv}
                disabled={importing}
              >
                Download sample CSV
              </button>

              {csvHeaders.length > 0 && (
                <div className="contacts-import-mapping">
                  <h3>Map CSV columns</h3>
                  {IMPORT_FIELDS.map((field) => (
                    <label key={field.key}>
                      {field.label}
                      {field.required ? " *" : ""}
                      <select
                        value={importMappings[field.key]}
                        onChange={(e) =>
                          onMappingChange(field.key, e.target.value)
                        }
                        required={field.required}
                        disabled={importing}
                      >
                        <option value="">Select column</option>
                        {csvHeaders.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}

                  <label>
                    Default country code
                    <input
                      type="text"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      placeholder="91"
                      disabled={importing}
                    />
                  </label>
                </div>
              )}

              <div className="contacts-import-actions">
                <button
                  type="button"
                  className="contacts-import-cancel"
                  onClick={closeImportModal}
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="contacts-import-submit"
                  disabled={importing || !csvFile}
                >
                  {importing ? "Submitting…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="contacts-pagination">
        <label>
          Per page
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <span>
          Page {page} of {totalPages} · {total} total
        </span>
        <div className="contacts-pagination-btns">
          <button
            type="button"
            className="biz-btn-outline"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className="biz-btn-outline"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Contacts;
